import React from 'react';
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Box,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface WidgetCardProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  viewAllLink?: string;
  viewAllLabel?: string;
  minHeight?: number | string;
  action?: React.ReactNode;
}

export const WidgetCard: React.FC<WidgetCardProps> = ({
  title,
  icon,
  children,
  isLoading = false,
  error = null,
  onRefresh,
  viewAllLink,
  viewAllLabel = 'View All',
  minHeight = 200,
  action,
}) => {
  const navigate = useNavigate();

  const handleViewAll = () => {
    if (viewAllLink) {
      navigate(viewAllLink);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header with icon and action buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
          pb: 0.5,
        }}
      >
        {/* Icon on the left */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>

        {/* Action buttons on the right */}
        <Box display="flex" alignItems="center" gap={0.5}>
          {action}
          {onRefresh && (
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={onRefresh} disabled={isLoading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {viewAllLink && (
            <Tooltip title={viewAllLabel}>
              <IconButton size="small" onClick={handleViewAll}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Title on its own row */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      <CardContent
        sx={{
          flex: 1,
          pt: 0,
          minHeight,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading ? (
          <Box>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="rectangular" height={60} sx={{ mt: 1 }} />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            color="error.main"
          >
            <Typography variant="body2">
              Failed to load data
            </Typography>
          </Box>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
};

export default WidgetCard;
